/**
 * Reconciliation — making the predicted body agree with the server's.
 *
 * On every snapshot:
 *   1. Take the authoritative state for THIS client's player.
 *   2. Drop every input the server has already processed (`lastProcessedSeq`).
 *   3. Ask: where did prediction think the body was at that same seq?
 *   4. If the answer is within epsilon, do nothing positional — a snap that
 *      moves you 2cm is a visible twitch that buys nothing.
 *   5. If it diverged, snap to the authoritative state and REPLAY every unacked
 *      input through the sim, so the correction lands with your last 100ms of
 *      movement already applied instead of teleporting you into the past.
 *   6. Bleed the REMAINING visual jump off over a few ticks — render-only.
 *      Replay covers errors your own inputs explain; it cannot cover a Seismic
 *      Slam knockback, a Gravity Well pull, or a mutator tick, and each of
 *      those used to land as a single-frame teleport of the mesh AND an equal
 *      camera jerk. The predictor's authoritative position snaps exactly as
 *      before — the offset lives only in `interpolate` — or the smoothing would
 *      reintroduce the mispredict it exists to hide. Past RECONCILE_HARD_SNAP
 *      the error is a genuine teleport and is shown as one.
 *
 * Step 5 is the difference between "responsive with occasional correction" and
 * "rubber-band". Skipping the replay is the classic mistake: the snap is
 * correct for a tick that is already ~3 ticks old, and every subsequent frame
 * re-diverges by exactly the inputs you dropped.
 *
 * Nothing here trusts the client. Every correction moves the client TOWARD the
 * server. There is no branch where a local value wins.
 */

import { RECONCILE_EPSILON, RECONCILE_HARD_SNAP } from '../config.js';

/** Scratch for the pre-snap on-screen position. One reconcile at a time. */
const _visual = { x: 0, y: 0, z: 0 };

export class Reconciler {
  constructor(predictor, inputSender) {
    this.predictor = predictor;
    this.sender = inputSender;

    /** seq -> predicted position after that seq was applied. */
    this._history = new Map();
    this.lastAckedSeq = 0;
    /** Diagnostics — surfaced in the debug overlay, never in a decision. */
    this.stats = { corrections: 0, hardSnaps: 0, lastError: 0, replayed: 0 };
  }

  /** Called right after the predictor steps a frame. */
  record(frame) {
    const p = this.predictor.player;
    this._history.set(frame.seq, { x: p.x, y: p.y, z: p.z });
    // Bound the map. Anything older than the ack is unreachable by definition.
    if (this._history.size > 256) {
      const cutoff = this.lastAckedSeq - 8;
      for (const seq of this._history.keys()) {
        if (seq < cutoff) this._history.delete(seq);
      }
    }
  }

  /**
   * Folds one authoritative snapshot in.
   *
   * `mine` is the PlayerSnapshot for this client. `snapshot.lastProcessedSeq` is
   * the server's ack, and the two must come from the same message: acking
   * against a stale seq replays inputs the server already applied, which
   * double-counts a dash.
   */
  apply(snapshot, mine) {
    const predictor = this.predictor;
    const ackSeq = snapshot.lastProcessedSeq;
    this.lastAckedSeq = ackSeq;

    this.sender.ack(ackSeq);

    const predictedAtAck = this._history.get(ackSeq);
    this._history.delete(ackSeq);

    // Nothing to compare against: either this is the first snapshot, or the ack
    // refers to a frame we already pruned. Snap — being right is cheap here.
    let error = Infinity;
    if (predictedAtAck) {
      const dx = predictedAtAck.x - mine.x;
      const dy = predictedAtAck.y - mine.y;
      const dz = predictedAtAck.z - mine.z;
      error = Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    this.stats.lastError = error === Infinity ? 0 : error;

    const diverged = error > RECONCILE_EPSILON;

    // Before any replay: the unacked inputs are about to be re-stepped, and
    // stepping them through an unmutated world is how a live mutator turned
    // every correction into a fresh mispredict.
    predictor.syncChaos(snapshot.chaos);

    // The server's tick governs cooldowns, so it is taken every snapshot
    // regardless of whether the position needed correcting.
    predictor.setTick(snapshot.tick);
    predictor.applySnapshot(mine, { snapPosition: diverged });

    if (!diverged) return false;

    this.stats.corrections += 1;
    if (error > RECONCILE_HARD_SNAP) this.stats.hardSnaps += 1;

    // Where the body was DRAWN this frame, taken before the replay rewrites
    // prev/curr: the gap between this and the corrected position is exactly the
    // jump the player would otherwise see in one frame.
    const vis = predictor.captureVisual(_visual);

    const unacked = this.sender.unacked();
    this.stats.replayed = unacked.length;
    for (const frame of unacked) {
      predictor.step(frame);
      const p = predictor.player;
      this._history.set(frame.seq, { x: p.x, y: p.y, z: p.z });
    }

    // A replay rewrote `prev` and `curr` several times over. Collapsing them
    // means the render interpolator does not lerp across the whole correction —
    // the authoritative snap is instant, and the frames after it are smooth.
    predictor.prev.x = predictor.curr.x = predictor.player.x;
    predictor.prev.y = predictor.curr.y = predictor.player.y;
    predictor.prev.z = predictor.curr.z = predictor.player.z;
    predictor.prev.yaw = predictor.curr.yaw = predictor.player.yaw;

    if (error <= RECONCILE_HARD_SNAP) {
      // The DRAWN body stays where it was this frame and bleeds onto the
      // corrected position over a few ticks. Hit tests and the sim never see
      // the offset — it exists only inside `interpolate`.
      predictor.beginCorrectionSmoothing(
        vis.x - predictor.curr.x,
        vis.y - predictor.curr.y,
        vis.z - predictor.curr.z
      );
    } else {
      // Includes error === Infinity (first snapshot, pruned history): initial
      // placement and true teleports are shown instantly, not slid into.
      predictor.clearCorrectionSmoothing();
    }

    return true;
  }

  reset() {
    this._history.clear();
    this.lastAckedSeq = 0;
  }
}
